import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';

interface DashboardStats {
  totalUsers: number;
  totalSeasons: number;
  totalTeams: number;
  totalMatches: number;
  totalPredictions: number;
  activeSeasons: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/admin/dashboard');
        setStats(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
            ← Back to Main Site
          </Link>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Admin Dashboard
          </h1>
          <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
            Manage your PredictorPro platform
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="card rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(59, 130, 246, 0.2)' }}>
                  <svg className="w-6 h-6" style={{ color: '#3b82f6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Users</p>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.totalUsers}</p>
                </div>
              </div>
            </div>

            <div className="card rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}>
                  <svg className="w-6 h-6" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h2m0 0h2m-2 0v4m6-6v-3a2 2 0 00-2-2h-2m2 5.5V12m0 0h2a2 2 0 012 2v3a2 2 0 01-2 2h-2m-4 0h2m-2 0v4" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Predictions</p>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.totalPredictions}</p>
                </div>
              </div>
            </div>


            <div className="card rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(234, 179, 8, 0.2)' }}>
                  <svg className="w-6 h-6" style={{ color: '#eab308' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>

                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Matches</p>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.totalMatches}</p>
                </div>
              </div>
            </div>

{/*             <div className="card rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                  <svg className="w-6 h-6" style={{ color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Seasons</p>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.totalSeasons}</p>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{stats.activeSeasons} active</p>
                </div>
              </div>
            </div> */}

{/*             <div className="card rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(168, 85, 247, 0.2)' }}>

                  <svg className="w-6 h-6" style={{ color: 'a855f7' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke="none" d="M0 0h24v24H0z"/><path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 4l6 2v5h-3v8a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1v-8h-3v-5l6 -2a3 3 0 0 0 6 0" /></svg>

                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Total Teams</p>
                  <p className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>{stats.totalTeams}</p>
                </div>
              </div>
            </div> */}



          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          <Link to="/admin/matches" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage Matches</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Create matches and enter results</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/news" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage News</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Create and publish news articles</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/trophies" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage Trophies</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Create trophies and award users</p>
              </div>

              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path  strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 21l8 0" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 17l0 4" /><path  strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4l10 0" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 4v8a5 5 0 0 1 -10 0v-8" /><path  strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /></svg>

            </div>
          </Link>


          <Link to="/admin/users" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage Users</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>View and edit user accounts</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/teams" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage Teams</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Add and edit football teams</p>
              </div>

              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke="none" d="M0 0h24v24H0z"/><path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 4l6 2v5h-3v8a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1v-8h-3v-5l6 -2a3 3 0 0 0 6 0" /></svg>
            </div>
          </Link>

          <Link to="/admin/seasons" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage Seasons</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Create and manage football seasons</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/competitions" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Manage Competitions</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Create and manage football competitions</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/matchgroups" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Match Groups</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Create and manage match weeks</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/match-distribution" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Match Distribution</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>View matches per match week</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </Link>



          <Link to="/admin/favourite-teams" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Favourite Teams</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>View team popularity analytics</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/newsletter" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Weekly Newsletter</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Send newsletters to all users</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </Link>

          <Link to="/admin/chat-moderation" className="card rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Chat Moderation</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Review and moderate league chat messages</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--highlight)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
          </Link>



          <div className="card rounded-lg shadow p-6" style={{ opacity: 0.6 }}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Reports</h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Analytics and insights</p>
              </div>
              <svg className="w-8 h-8" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 